import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, User, Search, PackageSearch, Bell, Heart, Sparkles } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useAuth } from "../context/AuthContext";
import api, { LOGO_URL } from "../lib/api";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "Build My Look", to: "/build-my-look" },
  { label: "Style My Home", to: "/style-my-home" },
  { label: "Style Quiz", to: "/quiz" },
  { label: "Track Order", to: "/track" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const { count } = useCart();
  const { count: wishCount } = useWishlist();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [newOrders, setNewOrders] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role !== "admin") return;
    let active = true;
    const poll = () =>
      api
        .get("/admin/new-count")
        .then((r) => active && setNewOrders(r.data.count))
        .catch(() => {});
    poll();
    const t = setInterval(poll, 30000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [user]);

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAFAF7]/85 border-b border-border/60"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img
              src={LOGO_URL}
              alt="Aethel Decor — Beyond Decor. Simply Elevated."
              className="h-14 sm:h-16 w-auto object-contain"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="text-sm tracking-wide text-foreground/80 hover:text-primary link-underline transition-colors"
                data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <button
              onClick={() => navigate("/shop")}
              className="hidden sm:block text-foreground/70 hover:text-primary transition-colors"
              aria-label="Search"
              data-testid="nav-search-btn"
            >
              <Search size={20} strokeWidth={1.5} />
            </button>

            {user ? (
              <div className="hidden md:flex items-center gap-4">
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    className="relative text-foreground/70 hover:text-primary transition-colors"
                    data-testid="nav-admin"
                    title="Admin dashboard"
                  >
                    <Bell size={20} strokeWidth={1.5} />
                    {newOrders > 0 && (
                      <span
                        className="absolute -top-2 -right-2 bg-accent text-accent-foreground text-[10px] font-bold h-5 w-5 rounded-full flex items-center justify-center"
                        data-testid="nav-new-orders"
                      >
                        {newOrders}
                      </span>
                    )}
                  </Link>
                )}
                <Link
                  to="/account"
                  className="text-foreground/70 hover:text-primary transition-colors"
                  data-testid="nav-account"
                >
                  <User size={20} strokeWidth={1.5} />
                </Link>
                <button
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                  className="text-xs tracking-wide text-foreground/60 hover:text-primary"
                  data-testid="nav-logout"
                >
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="hidden md:block text-sm tracking-wide text-foreground/80 hover:text-primary link-underline"
                data-testid="nav-login"
              >
                Login
              </Link>
            )}

            <Link
              to="/wishlist"
              className="relative text-foreground/80 hover:text-primary transition-colors"
              data-testid="nav-wishlist"
              title="Wishlist"
            >
              <Heart size={21} strokeWidth={1.5} />
              {wishCount > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-accent text-accent-foreground text-[10px] font-semibold h-5 w-5 rounded-full flex items-center justify-center"
                  data-testid="wishlist-count"
                >
                  {wishCount}
                </span>
              )}
            </Link>

            <Link
              to="/cart"
              className="relative text-foreground/80 hover:text-primary transition-colors"
              data-testid="nav-cart"
            >
              <ShoppingBag size={22} strokeWidth={1.5} />
              {count > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[10px] font-semibold h-5 w-5 rounded-full flex items-center justify-center"
                  data-testid="cart-count"
                >
                  {count}
                </span>
              )}
            </Link>

            <button
              className="md:hidden text-foreground"
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-toggle"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border/60 bg-[#FAFAF7] px-5 py-4 space-y-3">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 py-2 text-foreground/80"
              data-testid={`mnav-${n.label.toLowerCase().replace(" ", "-")}`}
            >
              {n.label === "Track Order" && <PackageSearch size={16} />}
              {n.label === "Style Quiz" && <Sparkles size={16} />}
              {n.label}
            </Link>
          ))}
          <Link
            to="/wishlist"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 py-2 text-foreground/80"
            data-testid="mnav-wishlist"
          >
            <Heart size={16} /> Wishlist{wishCount > 0 ? ` (${wishCount})` : ""}
          </Link>
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setOpen(false)} className="block py-2 text-accent font-semibold">Admin</Link>
              )}
              <Link to="/account" onClick={() => setOpen(false)} className="block py-2 text-foreground/80">My Account</Link>
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="block py-2 text-foreground/60">Logout</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="block py-2 text-foreground/80">Login</Link>
          )}
        </div>
      )}
    </header>
  );
}
